/**
 * File-name comparison for files selected together.
 * Keep in sync with backend `duplicateIdentity.normalizeFileName`.
 */

export type DuplicateReason =
  | 'same-file'
  | 'same-name-and-size'
  | 'same-eway-bill-no'
  | 'same-document-no'
  | 'same-invoice-no'
  | 'same-pod-no';

/** Number matches that ask the user to continue or cancel. */
export const CONFIRM_REASONS: DuplicateReason[] = [
  'same-eway-bill-no',
  'same-document-no',
  'same-invoice-no',
  'same-pod-no',
];

export function isConfirmReason(reason: DuplicateReason): boolean {
  return CONFIRM_REASONS.includes(reason);
}

export function normalizeFileName(fileName: string): string {
  const trimmed = fileName.trim().toLowerCase().replace(/\s+/g, ' ');
  const baseName = trimmed.split(/[/\\]/).pop() || trimmed;
  const dot = baseName.lastIndexOf('.');
  const ext = dot > 0 ? baseName.slice(dot) : '';
  let base = (dot > 0 ? baseName.slice(0, dot) : baseName).trim();
  let previous = '';
  while (base !== previous) {
    previous = base;
    base = base
      .replace(/\s*\(\d+\)\s*$/, '')
      .replace(/\s*-\s*copy\s*$/, '')
      .replace(/\s+copy\s*$/, '')
      .replace(/\s*\(copy\)\s*$/, '')
      .trim();
  }
  return `${base}${ext}`;
}

export function pageCountsConflict(left?: number, right?: number): boolean {
  if (left === undefined || right === undefined) return false;
  if (!Number.isInteger(left) || !Number.isInteger(right)) return false;
  if (left < 1 || right < 1) return false;
  return left !== right;
}

export function sameIdentities(left: string[] | undefined, right: string[] | undefined): boolean {
  if (!left || !right) return false;
  const other = new Set(right);
  return left.some((value) => other.has(value));
}

export function siblingReasons(input: {
  contentHash?: string;
  name: string;
  size: number;
  pageCount?: number;
  ewayBillNumbers?: string[];
  documentNumbers?: string[];
  invoiceNumbers?: string[];
  podNumbers?: string[];
}, other: {
  contentHash?: string;
  name: string;
  size: number;
  pageCount?: number;
  ewayBillNumbers?: string[];
  documentNumbers?: string[];
  invoiceNumbers?: string[];
  podNumbers?: string[];
}): DuplicateReason[] {
  const reasons: DuplicateReason[] = [];
  if (input.contentHash && input.contentHash === other.contentHash) {
    reasons.push('same-file');
  }
  if (
    input.size > 0 &&
    input.size === other.size &&
    normalizeFileName(input.name) === normalizeFileName(other.name) &&
    !pageCountsConflict(input.pageCount, other.pageCount)
  ) {
    reasons.push('same-name-and-size');
  }
  if (sameIdentities(input.ewayBillNumbers, other.ewayBillNumbers)) {
    reasons.push('same-eway-bill-no');
  }
  if (sameIdentities(input.documentNumbers, other.documentNumbers)) {
    reasons.push('same-document-no');
  }
  if (sameIdentities(input.invoiceNumbers, other.invoiceNumbers)) {
    reasons.push('same-invoice-no');
  }
  if (sameIdentities(input.podNumbers, other.podNumbers)) {
    reasons.push('same-pod-no');
  }
  return reasons;
}
